import { createSelector, Selector } from '@reduxjs/toolkit'
import { flatten, isNil, uniq } from 'lodash-es'
import {
  Application,
  EntityStatus,
  EntityType,
  Organisation,
  OrganisationMeta,
  OrganisationMetaDetail,
  ProductPermission,
  ProductSector,
  Status
} from 'api'
import { OrganisationHelper, ProductHelper, ProductSectorHelper, TradingEntityHelper } from 'model'
import { GlobalState } from 'store'
import { compareInsensitive, notNilFilter, ParametricSelector } from 'utils'
import { createAuthorisedUserSelector } from '../connectionSelectors'
import { createAllEntitiesSelector, createAllEntityUpdatesSelector, createEntityGetterSelector } from '../entitySelectors'
import {
  createActiveTradingEntitiesByOrganisationIdGetterSelector,
  createActiveTradingEntityIdsByOrganisationIdGetterSelector,
  createTradingEntityWithAliasGetterSelector
} from '../tradingEntitySelectors/tradingEntitySelectors'
import { isCurrentOrganisationBuySideSelector, isCurrentOrganisationSellSideSelector } from './organisationSelectors'
import { allKnownProductSectorsGetterSelector } from 'selectors/productSelectors'

export const organisationMetaDetailsSelector: Selector<GlobalState, OrganisationMetaDetail[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.ORGANISATION_META),
  createEntityGetterSelector(EntityType.ORGANISATION),
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  createTradingEntityWithAliasGetterSelector(),
  createEntityGetterSelector(EntityType.PRODUCT),
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  createEntityGetterSelector(EntityType.USER),
  (organisationMetaUpdates, getOrganisation, getTradingEntity, getTradingEntityWithAlias, getProduct, getProductSector, getUser) =>
    organisationMetaUpdates.map(update => {
      const entity = update.entity
      const ownerOrganisation = getOrganisation(entity.ownerOrganisationId)
      const ownerTradingEntity = !isNil(entity.ownerTradingEntityId) ? getTradingEntity(entity.ownerTradingEntityId) : null
      const subjectOrganisation = !isNil(entity.subjectOrganisationId) ? getOrganisation(entity.subjectOrganisationId) : null
      const subjectTradingEntity = !isNil(entity.subjectTradingEntityId)
        ? !isNil(entity.ownerTradingEntityId)
          ? getTradingEntityWithAlias(entity.ownerTradingEntityId, entity.subjectTradingEntityId)
          : getTradingEntity(entity.subjectTradingEntityId)
        : null
      const product = !isNil(entity.productId) ? getProduct(entity.productId) : null
      const productSector = !isNil(entity.productSectorId) ? getProductSector(entity.productSectorId) : null
      const primaryCoverUser = !isNil(entity.primaryCoverUserId) ? getUser(entity.primaryCoverUserId) : null
      const secondaryCoverUser = !isNil(entity.secondaryCoverUserId) ? getUser(entity.secondaryCoverUserId) : null
      const availableEvents = update.availableEvents

      return {
        organisationMeta: entity,
        ownerOrganisation,
        ownerTradingEntity,
        subjectOrganisation,
        subjectTradingEntity,
        product,
        productSector,
        primaryCoverUser,
        secondaryCoverUser,
        availableEvents
      }
    })
)

export const organisationMetaDetailsGetterSelector: Selector<
  GlobalState,
  (organisationMetas: OrganisationMeta[]) => OrganisationMetaDetail[]
> = createSelector(
  [
    createEntityGetterSelector(EntityType.ORGANISATION),
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    createTradingEntityWithAliasGetterSelector(),
    createEntityGetterSelector(EntityType.PRODUCT),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    createEntityGetterSelector(EntityType.USER)
  ],
  (getOrganisation, getTradingEntity, getTradingEntityWithAlias, getProduct, getProductSector, getUser) => organisationMetas =>
    organisationMetas.map(organisationMeta => {
      const ownerOrganisation = getOrganisation(organisationMeta.ownerOrganisationId)
      const ownerTradingEntity = !isNil(organisationMeta.ownerTradingEntityId)
        ? getTradingEntity(organisationMeta.ownerTradingEntityId)
        : null
      const subjectOrganisation = !isNil(organisationMeta.subjectOrganisationId)
        ? getOrganisation(organisationMeta.subjectOrganisationId)
        : null
      const subjectTradingEntity = !isNil(organisationMeta.subjectTradingEntityId)
        ? !isNil(organisationMeta.ownerTradingEntityId)
          ? getTradingEntityWithAlias(organisationMeta.ownerTradingEntityId, organisationMeta.subjectTradingEntityId)
          : getTradingEntity(organisationMeta.subjectTradingEntityId)
        : null
      const product = !isNil(organisationMeta.productId) ? getProduct(organisationMeta.productId) : null
      const productSector = !isNil(organisationMeta.productSectorId) ? getProductSector(organisationMeta.productSectorId) : null
      const primaryCoverUser = !isNil(organisationMeta.primaryCoverUserId) ? getUser(organisationMeta.primaryCoverUserId) : null
      const secondaryCoverUser = !isNil(organisationMeta.secondaryCoverUserId) ? getUser(organisationMeta.secondaryCoverUserId) : null

      return {
        organisationMeta,
        ownerOrganisation,
        ownerTradingEntity,
        subjectOrganisation,
        subjectTradingEntity,
        product,
        productSector,
        primaryCoverUser,
        secondaryCoverUser,
        availableEvents: []
      }
    })
)

export const createOrganisationMetasByOrganisationGetterSelector: Selector<
  GlobalState,
  (organisationId: string) => OrganisationMeta[]
> = createSelector(createAllEntitiesSelector(EntityType.ORGANISATION_META), organisationMetas => (organisationId: string) =>
  organisationMetas.filter(organisationMeta => organisationMeta.ownerOrganisationId === organisationId)
)

export function createOrganisationMetaDetailSelector<TProps>(
  organisationIdSelector: ParametricSelector<{}, TProps, string>
): ParametricSelector<GlobalState, TProps, OrganisationMetaDetail[]> {
  return createSelector(organisationIdSelector, organisationMetaDetailsSelector, (organisationId, organisationMetaDetails) =>
    organisationMetaDetails.filter(organisationMetaDetail => organisationMetaDetail.organisationMeta.ownerOrganisationId === organisationId)
  )
}

export interface OrganisationMetaSelectorProps {
  organisationId?: string
  selectedTradingEntityId?: string | null
  selectedCounterpartyOrganisationId?: string | null
  selectedProductId?: string | null
  selectedProductIds?: string[]
}

const orgMetaOrganisationIdSelector = (_: {}, props: OrganisationMetaSelectorProps) => props.organisationId
const orgMetaTradingEntityIdSelector = (_: {}, props: OrganisationMetaSelectorProps) => props.selectedTradingEntityId
const orgMetaCounterpartyOrganisationIdSelector = (_: {}, props: OrganisationMetaSelectorProps) => props.selectedCounterpartyOrganisationId
const orgMetaProductIdSelector = (_: {}, props: OrganisationMetaSelectorProps) => props.selectedProductId
const orgMetaProductIdsSelector = (_: {}, props: OrganisationMetaSelectorProps) => props.selectedProductIds

export const organisationMetaTradingEntityIdsSelector: ParametricSelector<
  GlobalState,
  OrganisationMetaSelectorProps,
  string[]
> = createSelector(
  orgMetaOrganisationIdSelector,
  createAuthorisedUserSelector(),
  createAllEntitiesSelector(EntityType.TRADING_ENTITY),
  (organisationId, authorisedUser, tradingEntities) =>
    tradingEntities
      .filter(tradingEntity => tradingEntity.organisationId === organisationId)
      .filter(tradingEntity => tradingEntity.status === EntityStatus.ENABLED)
      .filter(
        tradingEntity =>
          authorisedUser.application === Application.ADMIN_GATEWAY ||
          authorisedUser.tradingEntityDetails.some(d => d.tradingEntityId === tradingEntity.id)
      )
      .sort((te1, te2) => compareInsensitive(TradingEntityHelper.getName(te1), TradingEntityHelper.getName(te2)))
      .map(tradingEntity => tradingEntity.id)
)

export const organisationMetaCounterpartyOrganisationIdsSelector: ParametricSelector<
  GlobalState,
  OrganisationMetaSelectorProps,
  string[]
> = createSelector(
  isCurrentOrganisationSellSideSelector,
  isCurrentOrganisationBuySideSelector,
  orgMetaOrganisationIdSelector,
  orgMetaTradingEntityIdSelector,
  createActiveTradingEntityIdsByOrganisationIdGetterSelector,
  createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP),
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  createEntityGetterSelector(EntityType.ORGANISATION),
  (
    isSellSide,
    isBuySide,
    organisationId,
    tradingEntityId,
    getTradingEntitiesByOrgId,
    tradingRelationships,
    getTradingEntity,
    getOrganisation
  ) => {
    const orgTradingEntityIds = !isNil(tradingEntityId)
      ? [tradingEntityId]
      : !isNil(organisationId)
      ? getTradingEntitiesByOrgId(organisationId)
      : []

    const counterpartyOrganisations = tradingRelationships
      .filter(
        tradingRelationship =>
          tradingRelationship.status === Status.APPROVED &&
          orgTradingEntityIds.includes(
            isSellSide && !isBuySide ? tradingRelationship.sellTradingEntity : tradingRelationship.buyTradingEntity
          )
      )
      .map(tradingRelationship => (isSellSide && !isBuySide ? tradingRelationship.buyTradingEntity : tradingRelationship.sellTradingEntity))
      .map(counterpartyTradingEntityId => getTradingEntity(counterpartyTradingEntityId))
      .map(counterpartyTradingEntity =>
        counterpartyTradingEntity === null ? null : getOrganisation(counterpartyTradingEntity.organisationId)
      )
      .filter(notNilFilter)

    return uniq(
      counterpartyOrganisations
        .sort((org1, org2) => compareInsensitive(OrganisationHelper.getName(org1), OrganisationHelper.getName(org2)))
        .map((counterpartyOrganisation: Organisation) => counterpartyOrganisation.id)
    )
  }
)

export const organisationMetaCounterpartyIdsSelector: ParametricSelector<
  GlobalState,
  OrganisationMetaSelectorProps,
  string[]
> = createSelector(
  orgMetaCounterpartyOrganisationIdSelector,
  createAllEntitiesSelector(EntityType.TRADING_ENTITY),
  (counterpartyOrganisationId, tradingEntities) => {
    if (!counterpartyOrganisationId) {
      return []
    }

    return tradingEntities
      .filter(tradingEntity => tradingEntity.organisationId === counterpartyOrganisationId)
      .filter(tradingEntity => tradingEntity.status === EntityStatus.ENABLED)
      .sort((te1, te2) => compareInsensitive(TradingEntityHelper.getName(te1), TradingEntityHelper.getName(te2)))
      .map(tradingEntity => tradingEntity.id)
  }
)

const organisationMetaPermissionedProductSectorsSelector: ParametricSelector<
  GlobalState,
  OrganisationMetaSelectorProps,
  ProductSector[]
> = createSelector(
  orgMetaOrganisationIdSelector,
  orgMetaTradingEntityIdSelector,
  createActiveTradingEntitiesByOrganisationIdGetterSelector,
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  allKnownProductSectorsGetterSelector,
  (organisationId, tradingEntityId, getTradingEntitiesByOrgId, getTradingEntity, productSectors) => {
    const tradingEntity = !isNil(tradingEntityId) ? getTradingEntity(tradingEntityId) : null
    const orgTradingEntities = !isNil(tradingEntity)
      ? [tradingEntity]
      : !isNil(organisationId)
      ? getTradingEntitiesByOrgId(organisationId)
      : []
    const productRights = flatten(orgTradingEntities.map(counterparty => counterparty.productRights))

    return productSectors.filter(productSector =>
      productRights.some(
        productRight => productRight.productSectorId === productSector.id && productRight.permission === ProductPermission.TRADE
      )
    )
  }
)

export const organisationMetaProductIdsSelector: ParametricSelector<GlobalState, OrganisationMetaSelectorProps, string[]> = createSelector(
  organisationMetaPermissionedProductSectorsSelector,
  createEntityGetterSelector(EntityType.PRODUCT),
  (permissionedProductSectors, getProduct) => {
    const products = uniq(permissionedProductSectors.map(productSector => getProduct(productSector.productId)).filter(notNilFilter))
    return products.sort((p1, p2) => compareInsensitive(ProductHelper.getName(p1), ProductHelper.getName(p2))).map(product => product.id)
  }
)

export const organisationMetaProductSectorIdsSelector: ParametricSelector<
  GlobalState,
  OrganisationMetaSelectorProps,
  string[]
> = createSelector(
  [organisationMetaPermissionedProductSectorsSelector, orgMetaProductIdSelector, orgMetaProductIdsSelector],
  (permissionedProductSectors, productId, productIds) => {
    if (isNil(productId) && (isNil(productIds) || productIds.length !== 1)) {
      return []
    }

    return permissionedProductSectors
      .filter(productSector => productSector.productId === (productId ?? productIds?.[0]))
      .sort((ps1, ps2) => compareInsensitive(ProductSectorHelper.getName(ps1), ProductSectorHelper.getName(ps2)))
      .map(productSector => productSector.id)
  }
)
